import {
  ColumnBase,
  ColumnReorderEvent,
  ColumnResizeArgs,
  GridDataResult,
  GridNavigableSettings,
  NavigationCell,
  NavigationRow,
  PagerSettings,
  ScrollMode,
  SelectableSettings,
  SortSettings,
} from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { DynamicServicedComponentDefinition } from 'shared/components/dynamic-component-wrapper/dynamic-serviced-component-wrapper.interface';
import { IPagination } from 'shared/models/pagination.model';
import { EditableType } from './editable-cell/editable-cell.interfaces';

// tslint:disable:no-inferrable-types
//Deprecated Use dynamic cell components.
export interface StatusIconDeprecated {
  [key: string]: { [key: string]: string };
}

// tslint:disable:no-inferrable-types
//Deprecated Use dynamic cell components.
export interface RowButtonsDeprecated {
  [key: string]: { type: string; size: string; label: string; showOnHover: boolean };
}

//Deprecated
export interface EditedCellDeprecated {
  value: any;
  id: string;
  dataPropertyName: string;
  activeCell: NavigationCell;
  activeRow: NavigationRow;
}

//Deprecated
export interface EditedItemsAggregateDeprecated {
  [tableId: string]: {
    [rowIndex: string]: {
      dataItemReference: any; // NavigationRow.dataItem (which is of type any)
      changedValues: {
        [dataItemProperty: string]: any | null;
      } | null;
    } | null;
  } | null;
}

/**
 * ErrorAndNoDataPanelConfig offers all the inputs you could desire to change the wording
 * This component will load on the screen where the table would normally display when a
 * few conditions are met.
 */
export class ErrorAndNoDataPanelConfig {
  displayErrorPanel: boolean; // false
  errorPanelMainMessage?: string;
  errorPanelSubMessage?: string;
  errorPanelReloadButtonText?: string;

  noDataMainMessage?: string;
  noDataSubMessage?: string;
  noDataReloadButtonText?: string;
  inCardStyling?: boolean = false;
}

export class NoDataMessageConfigDeprecated {
  title: string;
  message: string;
}

export enum TableHeight {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export interface ExcelWorkbook {
  data: any[];               
  columnConfig: ColumnsConfig;  
  sheetName: string;         
}

export interface GMSExportData {
  sheets: ExcelWorkbook[];  
}

export class TableConfig {
  id: string;
  titleHeading: string;

  alternatingRowStyle?: boolean; // make Design agree on what this should be, look into making this transparent.
  alternateAlternatingRowStyle?: boolean; // same as alternatingRowStyle but alternate colors start on even rows, not odd
  alternateHeadingStyle?: boolean;
  highContrastColumns?: boolean;
  transparentStyle?: boolean; // allows the table's backgrounds to become transparent.
  displayTableStyle?: boolean; // makes table look like display only
  noMargin?: boolean;
  errorPanelConfig?: ErrorAndNoDataPanelConfig;
  allowColumnHeaderWrap?: boolean;
  autoResize?: boolean;
  pageable?: PagerSettings | boolean = false;
  pagination?: IPagination;
  pageOffset?: number = 0;
  skip?: number;
  sortable?: SortSettings;
  sort?: Array<SortDescriptor>;
  persistPreviousSort?: boolean = true;
  selectable?: SelectableSettings | boolean = false;
  showTotal?: boolean = true;
  clickable?: boolean;
  scrollable?: ScrollMode;
  navigable?: true | GridNavigableSettings[] = true; //https://www.telerik.com/kendo-angular-ui/components/grid/keyboard-navigation/
  reorderable?: boolean = false;
  resizable?: boolean = true;
  displayFooter?: boolean = true;
  tableOverflow?: boolean = false;

  height?: TableHeight | number; //needed for scrolling (fixed headers) / virtual scrolling.
  rowHeight?: number; //needed for virtual scrolling.
  normalErrorMessageDeprecated?: string =
    'Unable to Retrieve Data. Please reload or try again later.'; // Use error panel config.
  noDataMessageDeprecated?: NoDataMessageConfigDeprecated; // Use error panel Config
}

export enum Alignment {
  RIGHT = 'right',
  CENTER = 'center',
  LEFT = 'left',
}

/**
 * @deprecated
 */
export interface EditableColumnDropdownPropertiesDeprecated {
  data: Array<any>;
  dataValueField: string;
  dataDisplayField: string;
  dataDescriptionField?: string;
  formGroupControlName: string;
}

/**
 * @notice will be reviewed for use with a dynamic component.
 */
export interface MultiRowHeaderProperties {
  headerValue: string;
  customColumnName: string;
  headerAlignment: Alignment;
  customIcon?: string;
}

export interface OverrideIcon {
  iconClass: string;
  iconText: string;
}

export class Column {
  groupWidth?: number;
  headers: Array<string>;
  sortOverride?: Array<string>;
  dynamicHeader?: DynamicServicedComponentDefinition; // use this to provide a dynamic header component.
  multiRowHeader?: MultiRowHeaderProperties; // Will be revisited in Dynamic header component.
  headerAlignment?: Alignment;
  dataFields: Array<string>;
  dataFieldAlignment?: Array<Alignment>;
  hideNullValue?: boolean = false;
  displayEmptyAsDash?: boolean = true;
  width?: number;
  disableResizing?: boolean = false;
  disableReordering?: boolean = false;
  disablePopoverFunctionality?: boolean = false;
  isFocusedColumn?: boolean;
  isSeparatorColumn?: boolean;
  isSortable?: Array<boolean>;
  isSticky?: boolean;
  columnFooterValues?: Array<string>;
  columnFooterAlignment?: Array<Alignment>;
  isBoldColumn?: boolean; // candidate to be implemented in a dynamic component, though this option is not 'bloat'
  deleteRowFunctionDeprecated?: boolean = false; // Needs to be implemented in a dynamic component.
  hoverTextDeprecated?: string; // Use Dynamic cell component.
  disableAppPopover?: boolean = false; // Use dynamic cell component.
  isDynamicTooltipColumnDeprecated?: boolean; // Needs to use a dynamic component.
  isTooltipColumnDeprecated?: boolean; // use a dynamic cell component.
  formatNumbersDeprecated?: boolean = false; // clean Data before passing it in.
  formatNegativeNumberWithParenthesisDeprecated?: boolean = false; // Needs to be implemented in a dynamic component.
  editableDropdownPropertiesDeprecated?: EditableColumnDropdownPropertiesDeprecated; // use dynamic cell component.
  positiveNegativeValueColorDeprecated?: Array<boolean>; // Needs to be implemented in a dynamic component.
  editableDeprecated?: Array<EditableType>; // Use Dynamic cell component
}

// Extended Column so the linter wouldn't complain, Do not use the columnGroup as a column...
export interface ColumnGroup {
  groupHeaderName: string;
  groupWidth?: number;
  alignment?: Alignment;
  columns: {
    [ColumnNumber: number]: Column;
  };
  isGroupedWithNoHeader?: boolean;
}

export interface ColumnsConfig {
  [ColumnNumber: number]: any; // use Column or if you need a group ColumnGroup;
}

// forces typing and allows for TSlint to suggest items to you
export const createColumns = (columnConfig: ColumnsConfig): ColumnsConfig => columnConfig;
export const createColumn = (column: Column): Column => column;
export const createColumnGroup = (columnGroup: ColumnGroup): ColumnGroup => columnGroup;
export const createTableConfig = (tableConfig: TableConfig): TableConfig => { return {...new TableConfig(), ...tableConfig} };

// tslint:disable-next-line: no-empty-interface
export interface TableData extends GridDataResult {}

export interface HeaderSortChangeEvent {
  sortDescriptors: Array<SortDescriptor>;
  stackedHeaderIndex: number;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface DirectionAndDataField {
  direction: SortDirection;
  dataField: string;
  currentlySorted: boolean;
}

export interface SortableColumn extends DirectionAndDataField {
  sortable: boolean;
}

export type SortableColumnConfig = Record<
  string,
  Array<SortableColumn[] | Array<SortableColumn[]>>
>;

/*
 * If it's a normal or stacked column, it will be [column index, null]
 * If it's a grouped column, it will be [group index, column index within group]
 */
export type ColumnIndex = [string, string];

export interface ColumnBaseWithDataField extends ColumnBase {
  field?: string;
}

export interface ColumnResizeArgsWithDataField extends ColumnResizeArgs {
  column: ColumnBaseWithDataField;
}

export type ColumnResizeEventWithDataField = Array<ColumnResizeArgsWithDataField>;
